<template>
    <vx-expandable-card>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div>
                    <vx-label size="md">Legal company name</vx-label>
                    <vx-input
                        v-model="form.company_name"
                        autofocus
                        size="lg"
                        data-test="company-name-input"
                        :error="displayFirstError(validate.company_name.$errors)"
                    ></vx-input>
                </div>

                <div>
                    <div class="flex items-center justify-between">
                        <vx-label size="md">DBA / display name</vx-label>
                        <vx-button
                            @click="form.display_name = form.company_name"
                            type="button"
                            size="xs"
                            color="muted"
                            data-test="copy-legal-name"
                        >
                            Same as legal name
                        </vx-button>
                    </div>
                    <vx-input
                        v-model="form.display_name"
                        size="lg"
                        data-test="display-name-input"
                        :error="displayFirstError(validate.display_name.$errors)"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">EIN</vx-label>
                    <vx-input
                        v-model="form.ein"
                        size="lg"
                        data-test="ein-input"
                        :error="displayFirstError(validate.ein.$errors)"
                    ></vx-input>
                    <p class="text-sm text-slate-500 mt-1">
                        A US EIN or a Canadian business number: nine digits, hyphens optional
                    </p>
                </div>

                <div data-test="vertical-select">
                    <vx-label size="md">Vertical</vx-label>
                    <vx-selectable
                        v-model="form.vertical"
                        size="lg"
                        placeholder="Select a vertical"
                        :options="props.enums?.verticals ?? []"
                        :reduce="(option: VerticalOption) => option.value"
                        :loading="null === props.enums"
                        :clearable="false"
                        :error="displayFirstError(validate.vertical.$errors)"
                    ></vx-selectable>
                </div>

                <div>
                    <vx-label size="md">Website</vx-label>
                    <vx-input
                        v-model="form.website"
                        size="lg"
                        data-test="website-input"
                        :error="displayFirstError(validate.website.$errors)"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Support email</vx-label>
                    <vx-input
                        v-model="form.email"
                        type="email"
                        size="lg"
                        data-test="email-input"
                        :error="displayFirstError(validate.email.$errors)"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Phone</vx-label>
                    <vx-input
                        v-model="form.phone"
                        type="tel"
                        size="lg"
                        placeholder="+15025550100"
                        data-test="phone-input"
                        :error="displayFirstError(validate.phone.$errors)"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Customer tag</vx-label>
                    <vx-input
                        :modelValue="form.customer_tag"
                        @update:modelValue="onCustomerTagInput"
                        size="lg"
                        data-test="customer-tag-input"
                        :error="displayFirstError(validate.customer_tag.$errors)"
                    ></vx-input>
                    <p class="text-sm text-slate-500 mt-1">Lets you find every registration for one customer</p>
                </div>

                <div data-test="brand-relationship-select">
                    <vx-label size="md">Brand relationship</vx-label>
                    <vx-selectable
                        v-model="form.brand_relationship"
                        size="lg"
                        :options="relationshipOptions"
                        :reduce="(option: RelationshipOption) => option.value"
                        :loading="null === props.enums"
                        :clearable="false"
                        :error="displayFirstError(validate.brand_relationship.$errors)"
                    ></vx-selectable>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { VxButton, VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { email, helpers, maxLength, required, url } from '@vuelidate/validators';
import { computed, toRef } from 'vue';
import { brandRelationshipLabels } from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import type { TcrBrandCreate, TcrBrandRelationship, TcrEnums } from '~/services/adminTenDlcRegistrationClient.types';
import { displayFirstError, phoneNumber } from '~/utils/validation';

type VerticalOption = TcrEnums['verticals'][number];
type RelationshipOption = { label: string; value: TcrBrandRelationship };

const props = defineProps<{ enums: TcrEnums | null; externalResults: Record<string, string[]> }>();
const form = defineModel<TcrBrandCreate>({ required: true });

const relationshipOptions = computed<RelationshipOption[]>(() =>
    (props.enums?.brand_relationships ?? []).map((value) => ({ value, label: brandRelationshipLabels[value] }))
);

const onCustomerTagInput = (value: string | number) => {
    form.value.customer_tag = String(value).toLowerCase();
};

// Mirrors BusinessRegistrationNumberRule.
const businessRegistrationNumber = helpers.withMessage(
    'Nine digits, hyphens optional',
    (value: string) => !helpers.req(value) || (/^[0-9-]+$/.test(value) && value.replace(/-/g, '').length === 9)
);

const validate = useVuelidate(
    {
        company_name: { required, maxLength: maxLength(255) },
        display_name: { required, maxLength: maxLength(255) },
        ein: { required, businessRegistrationNumber },
        vertical: { required },
        website: { url },
        email: { required, email, maxLength: maxLength(100) },
        phone: { required, phoneNumber },
        customer_tag: {
            maxLength: maxLength(50),
            tag: helpers.withMessage('Lowercase letters and digits only', helpers.regex(/^[a-z0-9]*$/)),
        },
        brand_relationship: { required },
    },
    form,
    { $externalResults: toRef(props, 'externalResults') }
);
</script>
